'use client'; // remove once feature flag is removed

import { Entry } from 'contentful';
import NextImage from 'next/image';

import { AppDownloadSection } from '../../components/AppDownloadSection/AppDownloadSection';
import { BlogPostSection } from '../../components/Blog/BlogPostSection/BlogPostSection';
import { Breadcrumbs } from '../../components/Breadcrumbs/Breadcrumbs';
import { Container } from '../../components/Container/Container';
import { IStateDataObjectSpecies } from '../../components/FindUSSpecies/helpers/buildStateDataSpecies';
import { USStateTopSpecies } from '../../components/FindUSSpecies/USStateTopSpecies';
import { FishingWaterCarousel } from '../../components/FishingWaterCarousel/FishingWaterCarousel';
import { Heading } from '../../components/Heading/Heading';
import { PlaceCarousel } from '../../components/PlaceCarousel/PlaceCarousel';
import { SEOLinksFooter } from '../../components/SEOFooter/SEOLinksFooter';
import { StateAgencySection } from '../../components/StateAgency/StateAgencySection';
import { TopWatersForRegionBySpecies } from '../../components/TopWatersForRegionBySpecies/TopWatersForRegionBySpecies';
import { ISEOWaterObjWithName } from '../../constants/fishingWaters/constants';
import { USStateObject } from '../../constants/USstates/constants';
import { getEdgesFromConnection, getNodesFromConnection } from '../../helpers/graphql';
import { getCountryUrl, getNewRegionUrl } from '../../helpers/urls';
import { TypeBlogPostSkeleton, TypeStateAgencySkeleton } from '../../interfaces/contentful';
import {
  GetRegion_entry,
  GetRegion_entry_places_edges,
  PlaceCarouselItem,
} from '../../interfaces/graphql';

import headerBackground from './assets/headerBackground.png';
import { AnglerRecommendedWaters } from './components/AnglerRecommendedWaters';
import { useRouter } from 'next/navigation';
import { useFeatureFlags } from '../../helpers/hooks/useFeatureFlags';
import { useEffect } from 'react';

export interface IRegionProps {
  region: GetRegion_entry;
  regionCode: string;
  usStates: USStateObject[];
  stateDataSpecies: IStateDataObjectSpecies[]; // todo remove when removing feature flag
  blogPostItems: Entry<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[] | undefined;
  stateAgencyItems: Entry<TypeStateAgencySkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>[] | null;
  anglerRecommendedWatersList: ISEOWaterObjWithName[];
  topSearchedWaters: ISEOWaterObjWithName[];
}

export const Region = ({
  region,
  regionCode,
  usStates,
  anglerRecommendedWatersList,
  stateDataSpecies,
  blogPostItems,
  stateAgencyItems,
  topSearchedWaters,
}: IRegionProps) => {
  const {
    country: { countryName, id: countryId },
    localName,
    topFishingWaters,
    places,
  } = region;

  const waters = getNodesFromConnection(topFishingWaters);
  const placeEdges = getEdgesFromConnection<PlaceCarouselItem, GetRegion_entry_places_edges>(
    places,
  );

  const usStateObj = usStates.find(state => state.stateID === regionCode.toUpperCase());
  const pageTitle = `Fishing in ${localName}`;

  const featureflags = useFeatureFlags(['newStatePages']);
  const router = useRouter();

  useEffect(() => {
    if (featureflags.newStatePages) {
      router.replace(getNewRegionUrl(countryId, regionCode, countryName, localName ?? ''));
    }
  }, [countryId, countryName, featureflags, localName, regionCode, router]);

  return (
    <>
      <Container className="-px-6 sm:-px-11 md:-px-14">
        <div className="relative w-full bg-cover p-4 text-center sm:px-11 md:px-14">
          <NextImage
            alt="most liked catch image"
            className="size-full object-cover"
            src={headerBackground}
            fill
            priority
            placeholder="blur"
            sizes="100vw"
          />
          <div className="relative flex items-center justify-between">
            <Heading as="h1" className="m-0 w-11/12 text-left text-white sm:w-1/2">
              {pageTitle}
            </Heading>
            {countryId === 'US' && usStateObj !== undefined && (
              <div className="flex justify-end">
                <NextImage
                  className="h-[100px] sm:h-full"
                  src={usStateObj.svgWhite.src}
                  alt={usStateObj.stateName}
                  width={128}
                  height={128}
                  aria-label={`${usStateObj.stateName} silhouette`}
                />
              </div>
            )}
          </div>
        </div>
      </Container>

      <Container>
        <Breadcrumbs
          items={[
            { label: countryName, link: getCountryUrl(countryId, countryName) },
            {
              label: localName || '',
              link: '',
            },
          ]}
        />
      </Container>
      {countryId === 'US' && (
        <>
          <TopWatersForRegionBySpecies
            regionId={`${countryId}.${regionCode}`}
            regionName={localName}
          />

          {stateAgencyItems && stateAgencyItems.length > 0 && (
            <StateAgencySection stateAgencyItems={stateAgencyItems} />
          )}

          <USStateTopSpecies stateName={localName} stateDataSpecies={stateDataSpecies} />

          {anglerRecommendedWatersList.length > 0 && localName && (
            <AnglerRecommendedWaters
              regionName={localName}
              anglerRecommendedWatersList={anglerRecommendedWatersList}
            />
          )}
        </>
      )}

      <FishingWaterCarousel
        dataTestId="region-top-waters"
        title="Top Fishing Waters"
        clickEventSource="region-top-waters"
        clickEventSourceId={`${countryId}-${regionCode}`}
        waters={waters}
      />

      {placeEdges.length > 0 && (
        <PlaceCarousel
          dataTestId="region-cities"
          title={`Cities in ${localName}`}
          clickEventSource="region-cities"
          clickEventSourceId={`${countryId}-${regionCode}`}
          placeEdges={placeEdges}
        />
      )}

      {blogPostItems && blogPostItems.length > 0 && (
        <BlogPostSection blogPostItems={blogPostItems} linkTo="/blog" regionId={regionCode} />
      )}

      <AppDownloadSection hasAppRating className="lg:mx-32" />

      <SEOLinksFooter topSearchedWaters={topSearchedWaters} />
    </>
  );
};
